import axios from 'axios';


const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 15000,
});

// Attach JWT from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('aays_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Global response error handler
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('aays_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authApi = {
  me:          ()            => api.get('/auth/me'),
  logout:      ()            => api.post('/auth/logout'),
  // SPA flow: send Azure AD access token, receive app JWT
  spaCallback: (accessToken) => api.post('/auth/spa-callback', { accessToken }),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const usersApi = {
  list:        (params) => api.get('/users', { params }),
  create:      (data)   => api.post('/users', data),
  get:         (id)     => api.get(`/users/${id}`),
  update:      (id, data) => api.patch(`/users/${id}`, data),
  delete:      (id)     => api.delete(`/users/${id}`),
  bulkDelete:  (ids)    => api.post('/users/bulk-delete', { ids }),
  bulkUpdate:  (data)   => api.patch('/users/bulk-update', data),
  getReportees:(id)     => api.get(`/users/${id}/reportees`),
  lopReport:   (params) => api.get('/users/reports/lop', { params }),
  lopReportDetails: (userId, params) => api.get(`/users/reports/lop/${userId}/details`, { params }),
  departments: ()       => api.get('/users/departments'),
  regions:     ()       => api.get('/users/regions'),
  countries:   ()       => api.get('/users/countries'),
};

// ── Attendance ────────────────────────────────────────────────────────────────
export const attendanceApi = {
  list:    (params) => api.get('/attendance', { params }),
  byDate:  (date)   => api.get(`/attendance/date/${date}`),
  upsert:  (data)   => api.post('/attendance', data),
  summary: (params) => api.get('/attendance/summary', { params }),
};

// ── Leaves ────────────────────────────────────────────────────────────────────
export const leavesApi = {
  list:    (params) => api.get('/leaves', { params }),
  balance: (params) => api.get('/leaves/balance', { params }),
  create:  (data)   => api.post('/leaves', data),
  review:  (id, data) => api.patch(`/leaves/${id}/review`, data),
  cancel:  (id)     => api.patch(`/leaves/${id}/cancel`),
};

// ── Holidays ──────────────────────────────────────────────────────────────────
export const holidaysApi = {
  list:   (params)   => api.get('/holidays', { params }),
  create: (data)     => api.post('/holidays', data),
  update: (id, data) => api.patch(`/holidays/${id}`, data),
  delete: (id)       => api.delete(`/holidays/${id}`),
};

// ── Leave Policies ────────────────────────────────────────────────────────────
export const leavePoliciesApi = {
  list:     ()           => api.get('/leave-policies'),
  get:      (id)         => api.get(`/leave-policies/${id}`),
  create:   (data)       => api.post('/leave-policies', data),
  update:   (id, data)   => api.put(`/leave-policies/${id}`, data),
  delete:   (id)         => api.delete(`/leave-policies/${id}`),
  assign:   (id, data)   => api.post(`/leave-policies/${id}/assign`, data),
};

// ── Import ────────────────────────────────────────────────────────────────────
export const importApi = {
  masterDataStream: (file, signal) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('aays_token');
    const headers = {
      'Authorization': token ? `Bearer ${token}` : undefined,
    };
    
    return fetch(api.defaults.baseURL + '/import/master-data-stream', {
      method: 'POST',
      body: formData,
      headers: headers,
      signal: signal,
    });
  },
};

export default api;
